import React, { useState } from 'react';
import { format } from 'date-fns';
import { registry } from '../registry';
import { useRsvp } from '@/src/modules/rsvp';
import { MapPin, Clock, Calendar, Heart, X } from 'lucide-react';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_modern_luxury_011',
  name: 'Modern Luxury Baby Shower',
  occasionType: 'BABY_SHOWER',
  description: 'A completely modern, editorial, soft minimal premium template without floral elements. Features warm ivory and champagne gold accents.',
  isPremium: true,
  tags: ['modern', 'minimal', 'luxury', 'baby-shower', 'editorial'],
  colorScheme: {
    primary: '#4A3F35',
    accent: '#D4AF37',
    bg: '#FAF9F6',
  },
};

const DecorationLayer = () => (
  <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
    {/* Clouds (Moved behind strings) */}
    {/* Top Left Cloud */}
    <div className="absolute top-[120px] left-[10px] w-[140px] opacity-90 pointer-events-none">
      <SvgWatercolorCloud className="w-full h-auto" style={{ transform: 'scaleX(-1)' }} />
    </div>

    {/* Top Right Cloud (Under the moon) */}
    <div className="absolute top-[170px] right-[30px] w-[110px] opacity-80 pointer-events-none z-[-1]">
      <SvgWatercolorCloud className="w-full h-auto" />
    </div>

    {/* Hanging Strings */}
    {/* String 1: left-[8%] */}
    <div className="absolute top-0 left-[8%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[8%] w-[1px] h-[100px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[95px] left-[8%] -ml-[7.5px] text-[#D4AF37] opacity-80 scale-50" />

    {/* String 2: left-[15%] - Pearl + Large Star */}
    <div className="absolute top-0 left-[15%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[15%] w-[1px] h-[200px] bg-[#D4AF37] opacity-50"></div>
    <div className="absolute top-[90px] left-[15%] w-2 h-2 rounded-full bg-[#D4AF37] -ml-[3.5px]"></div>
    <GoldStar className="absolute top-[195px] left-[15%] -ml-[7.5px] text-[#D4AF37] opacity-90 scale-100" />

    {/* String 3: left-[25%] */}
    <div className="absolute top-0 left-[25%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[25%] w-[1px] h-[80px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[75px] left-[25%] -ml-[7.5px] text-[#D4AF37] opacity-80 scale-50" />

    {/* String 4: left-[35%] */}
    <div className="absolute top-0 left-[35%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[35%] w-[1px] h-[140px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[135px] left-[35%] -ml-[7.5px] text-[#D4AF37] opacity-80 scale-50" />

    {/* String 5: left-[45%] */}
    <div className="absolute top-0 left-[45%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[45%] w-[1px] h-[60px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[55px] left-[45%] -ml-[7.5px] text-[#D4AF37] opacity-80 scale-50" />

    {/* String 6: left-[58%] - Medium Star */}
    <div className="absolute top-0 left-[58%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[58%] w-[1px] h-[130px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[125px] left-[58%] -ml-[7.5px] text-[#D4AF37] opacity-85 scale-75" />

    {/* String 7: left-[68%] - Two Pearls + Small Star */}
    <div className="absolute top-0 left-[68%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[68%] w-[1px] h-[200px] bg-[#D4AF37] opacity-50"></div>
    <div className="absolute top-[70px] left-[68%] w-2 h-2 rounded-full bg-[#D4AF37] -ml-[3.5px]"></div>
    <div className="absolute top-[130px] left-[68%] w-1.5 h-1.5 rounded-full bg-[#D4AF37] -ml-[2.5px]"></div>
    <GoldStar className="absolute top-[195px] left-[68%] -ml-[7.5px] text-[#D4AF37] opacity-80 scale-50" />

    {/* String 8: left-[78%] */}
    <div className="absolute top-0 left-[78%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[78%] w-[1px] h-[90px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[85px] left-[78%] -ml-[7.5px] text-[#D4AF37] opacity-80 scale-50" />

    {/* String 9: left-[88%] - Large Star */}
    <div className="absolute top-0 left-[88%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[88%] w-[1px] h-[170px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[165px] left-[88%] -ml-[7.5px] text-[#D4AF37] opacity-90 scale-100" />

    {/* String 10: left-[95%] */}
    <div className="absolute top-0 left-[95%] w-1 h-1 rounded-full bg-[#D4AF37] -ml-[1.5px]"></div>
    <div className="absolute top-0 left-[95%] w-[1px] h-[110px] bg-[#D4AF37] opacity-50"></div>
    <GoldStar className="absolute top-[105px] left-[95%] -ml-[7.5px] text-[#D4AF37] opacity-80 scale-50" />

    {/* Moon (Upper Right: Top 55px, Right 70px) */}
    <div className="absolute top-[55px] right-[70px] w-[140px] h-[140px] opacity-95">
      <svg className="w-full h-full" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
         <g transform="rotate(35 50 50)" filter="url(#moonGlow)">
           <path d="M 50 10 A 40 40 0 1 1 50 90 A 48 48 0 0 0 50 10 Z" fill="url(#moonGrad2)" />
         </g>
         <defs>
           <filter id="moonGlow" x="-20%" y="-20%" width="140%" height="140%">
             <feDropShadow dx="0" dy="8" stdDeviation="6" floodColor="#D4AF37" floodOpacity="0.25" />
           </filter>
           <radialGradient id="moonGrad2" cx="70%" cy="30%" r="70%" fx="80%" fy="20%">
             <stop offset="0%" stopColor="#F5EAE0" />
             <stop offset="25%" stopColor="#E1C699" />
             <stop offset="65%" stopColor="#D4AF37" />
             <stop offset="100%" stopColor="#B38A2A" />
           </radialGradient>
         </defs>
      </svg>
    </div>
    
    <FloatingStars />
  </div>
);

const GoldStar = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L14.24 9.76L22 12L14.24 14.24L12 22L9.76 14.24L2 12L9.76 9.76L12 2Z" fill="currentColor" />
  </svg>
);

const SvgWatercolorCloud = ({ className, style }: { className?: string, style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="watercolorEdge" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
        <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="4" result="noise" />
        <feDisplacementMap in="blur" in2="noise" scale="12" xChannelSelector="R" yChannelSelector="G" result="displaced" />
        <feDropShadow in="displaced" dx="2" dy="6" stdDeviation="6" floodColor="#4A3F35" floodOpacity="0.04" result="shadowed" />
      </filter>
      <linearGradient id="cloudGradBase" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#EADECB" stopOpacity="0.9" />
        <stop offset="50%" stopColor="#D9CBB4" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#C4B49C" stopOpacity="0.5" />
      </linearGradient>
    </defs>
    <g filter="url(#watercolorEdge)">
      {/* Base fluffy shape */}
      <path d="M 40 90 C 0 90 0 30 50 30 C 70 -10 140 -10 160 30 C 210 30 210 90 170 90 C 130 105 80 105 40 90 Z" fill="url(#cloudGradBase)" />
      {/* Inner highlights */}
      <path d="M 50 80 C 20 80 20 40 60 40 C 80 5 130 5 150 40 C 190 40 190 80 160 80 C 120 90 80 90 50 80 Z" fill="#F2EBE1" fillOpacity="0.5" />
      <path d="M 60 70 C 40 70 40 50 70 50 C 90 25 120 25 140 50 C 170 50 170 70 150 70 C 110 75 80 75 60 70 Z" fill="#EADECB" fillOpacity="0.4" />
    </g>
  </svg>
);

const OutlineStar = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L14.24 9.76L22 12L14.24 14.24L12 22L9.76 14.24L2 12L9.76 9.76L12 2Z" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const FloatingStars = () => {
  const stars = [
    // Top area (above 300px)
    { top: '8%', left: '12%', size: 16, opacity: 40, outline: false },
    { top: '15%', left: '35%', size: 12, opacity: 30, outline: true },
    { top: '12%', left: '75%', size: 18, opacity: 50, outline: false },
    { top: '22%', left: '20%', size: 14, opacity: 20, outline: true },
    { top: '25%', left: '85%', size: 13, opacity: 40, outline: false },
    { top: '28%', left: '15%', size: 16, opacity: 35, outline: true },
    // Left margin (below 300px)
    { top: '35%', left: '8%', size: 14, opacity: 25, outline: false },
    { top: '45%', left: '12%', size: 12, opacity: 40, outline: true },
    { top: '55%', left: '7%', size: 18, opacity: 30, outline: false },
    { top: '65%', left: '10%', size: 13, opacity: 50, outline: true },
    { top: '75%', left: '8%', size: 16, opacity: 20, outline: false },
    { top: '85%', left: '12%', size: 12, opacity: 45, outline: true },
    // Right margin (below 300px)
    { top: '38%', left: '88%', size: 16, opacity: 30, outline: true },
    { top: '48%', left: '92%', size: 13, opacity: 40, outline: false },
    { top: '58%', left: '85%', size: 18, opacity: 25, outline: true },
    { top: '68%', left: '90%', size: 12, opacity: 50, outline: false },
    { top: '78%', left: '86%', size: 14, opacity: 35, outline: true },
    { top: '88%', left: '92%', size: 16, opacity: 20, outline: false },
    // Bottom area
    { top: '92%', left: '25%', size: 18, opacity: 30, outline: true },
    { top: '95%', left: '45%', size: 12, opacity: 40, outline: false },
    { top: '93%', left: '65%', size: 16, opacity: 25, outline: true },
    { top: '96%', left: '75%', size: 13, opacity: 50, outline: false },
    { top: '89%', left: '35%', size: 14, opacity: 20, outline: true },
  ];

  return (
    <>
      {stars.map((s, i) => (
        <div 
          key={i} 
          className="absolute text-[#D4AF37] pointer-events-none"
          style={{ 
            top: s.top, 
            left: s.left, 
            width: `${s.size}px`, 
            height: `${s.size}px`,
            opacity: s.opacity / 100 
          }}
        >
          {s.outline ? <OutlineStar className="w-full h-full" /> : <GoldStar className="w-full h-full" />}
        </div>
      ))}
    </>
  );
};

const PremiumRsvpModal = ({ invitationId, isLive, onClose }: { invitationId?: string | null, isLive: boolean, onClose: () => void }) => {
  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [guests, setGuests] = useState(1);
  const [attending, setAttending] = useState<'ACCEPTED' | 'DECLINED'>('ACCEPTED');
  const [message, setMessage] = useState('');
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalMessage = phone ? `Phone: ${phone}\n${message}` : message;
    await submit({
      guestName: name,
      guestEmail: email || undefined,
      status: attending,
      headcount: attending === 'DECLINED' ? 1 : guests,
      message: finalMessage || undefined,
    });
  };

  if (state === 'success') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[#FAF9F6]/80 backdrop-blur-md" onClick={onClose}></div>
        <div className="relative z-10 w-full max-w-md bg-white/40 backdrop-blur-xl border border-white/60 shadow-[0_20px_60px_rgba(212,175,55,0.15)] rounded-[2rem] overflow-hidden p-10 text-center">
          <p className="font-serif text-3xl text-[#4A3F35] mb-4">Thank You!</p>
          <p className="text-[#4A3F35]/70 font-sans tracking-wide">Your response has been seamlessly recorded.</p>
          <button onClick={onClose} className="mt-8 px-8 py-3 rounded-full border border-[#D4AF37]/50 text-[#4A3F35] hover:border-[#D4AF37] uppercase tracking-widest text-xs hover:bg-[#D4AF37] hover:text-white transition-all shadow-sm">Close</button>
        </div>
      </div>
    );
  }

  const inputClasses = "w-full bg-white/50 border border-white/60 rounded-xl px-5 py-4 text-[#4A3F35] placeholder:text-[#4A3F35]/50 focus:outline-none focus:border-[#D4AF37]/50 focus:bg-white/80 focus:ring-4 focus:ring-[#D4AF37]/10 transition-all font-sans text-sm backdrop-blur-md shadow-sm";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8">
      <div className="absolute inset-0 bg-[#FAF9F6]/80 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative z-10 w-full max-w-xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-[0_30px_80px_rgba(212,175,55,0.2)] rounded-[2.5rem] overflow-hidden p-8 sm:p-12">
        <button type="button" onClick={onClose} className="absolute top-6 right-6 text-[#4A3F35]/40 hover:text-[#4A3F35] transition-colors"><X className="w-6 h-6" /></button>
        
        <h3 className="text-3xl sm:text-4xl font-serif text-[#4A3F35] text-center mb-10 font-light tracking-wide">Are You Attending?</h3>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input type="text" placeholder="Full Name" required value={name} onChange={(e) => setName(e.target.value)} className={inputClasses} />
            <input type="tel" placeholder="Phone Number" required value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClasses} />
          </div>
          
          <input type="email" placeholder="Email Address" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClasses} />
          
          <div className="flex flex-col sm:flex-row gap-5">
            <div className="flex-1 bg-white/50 border border-white/60 rounded-xl p-1.5 flex backdrop-blur-md shadow-sm">
              <button type="button" onClick={() => setAttending('ACCEPTED')} className={`flex-1 py-3 rounded-lg text-xs sm:text-sm font-serif tracking-widest uppercase transition-all ${attending === 'ACCEPTED' ? 'bg-gradient-to-r from-[#C5A059] to-[#E6D0A7] text-white shadow-md' : 'text-[#4A3F35]/60 hover:text-[#4A3F35]'}`}>Joyfully Accept</button>
              <button type="button" onClick={() => setAttending('DECLINED')} className={`flex-1 py-3 rounded-lg text-xs sm:text-sm font-serif tracking-widest uppercase transition-all ${attending === 'DECLINED' ? 'bg-gradient-to-r from-[#C5A059] to-[#E6D0A7] text-white shadow-md' : 'text-[#4A3F35]/60 hover:text-[#4A3F35]'}`}>Regretfully Decline</button>
            </div>
            
            {attending === 'ACCEPTED' && (
              <div className="flex items-center justify-between sm:justify-center gap-4 bg-white/50 border border-white/60 rounded-xl px-6 py-3 backdrop-blur-md shadow-sm sm:w-1/3">
                <span className="text-xs uppercase tracking-widest text-[#4A3F35]/70">Guests</span>
                <div className="flex items-center gap-4">
                  <button type="button" onClick={() => setGuests(Math.max(1, guests - 1))} className="text-[#4A3F35]/50 hover:text-[#4A3F35] text-lg">-</button>
                  <span className="text-[#4A3F35] font-serif text-lg">{guests}</span>
                  <button type="button" onClick={() => setGuests(guests + 1)} className="text-[#4A3F35]/50 hover:text-[#4A3F35] text-lg">+</button>
                </div>
              </div>
            )}
          </div>
          
          <textarea placeholder="Leave a message for the host..." rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputClasses} resize-none`} />
          
          {state === 'error' && <p className="text-red-400 text-sm text-center">{errorMessage}</p>}
          
          <button type="submit" disabled={state === 'loading'} className="mt-6 w-full py-5 rounded-full bg-gradient-to-r from-[#C5A059] via-[#E6D0A7] to-[#C5A059] bg-[length:200%_auto] hover:bg-[position:right_center] text-white font-serif uppercase tracking-[0.25em] text-[13px] sm:text-sm shadow-[0_10px_30px_rgba(197,160,89,0.3)] hover:shadow-[0_15px_40px_rgba(197,160,89,0.5)] hover:-translate-y-1 transition-all duration-700 disabled:opacity-50 border border-white/20">
            {state === 'loading' ? 'Sending...' : 'Send RSVP'}
          </button>
        </form>
      </div>
    </div>
  );
};

export function ModernLuxuryBabyShower({ data, isLive = false, invitationId }: { data: InvitationData, isLive?: boolean, invitationId?: string }) {
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const baby = data.customData?.baby || {};

  const hostName = baby.hostName || data.hostName || 'Emma\nHarrington';
  const hostedBy = baby.hostedBy || data.hostName || 'The Harrington Family';
  const eventDate = data.eventDate ? new Date(data.eventDate) : new Date('2026-08-03T16:00:00Z');
  const eventTime = baby.eventTime || '2:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'The Stardust Pavilion';
  const message = baby.message || 'We can\'t wait to celebrate with you';
  const dressCode = baby.showDressCode !== false ? baby.dressCode : undefined;

  return (
    <div className="relative w-full bg-white text-[#4A3F35] font-sans selection:bg-[#D4AF37]/20 flex justify-center items-center min-h-[100dvh] z-0">
      
      {/* Premium Outer Container (The Invitation Card) */}
      <div className="relative w-full max-w-2xl bg-[#FDFCF9] shadow-[0_0_40px_rgba(0,0,0,0.03)] flex flex-col pt-[20px] pb-[40px] px-[40px] box-border min-h-screen overflow-hidden">
        
        {/* Background Textures inside the card */}
        <div className="absolute inset-0 pointer-events-none z-[0] opacity-60 mix-blend-multiply" 
          style={{
            background: 'radial-gradient(circle at 20% 30%, #F4E8D1 0%, transparent 50%), radial-gradient(circle at 80% 70%, #F5EAE0 0%, transparent 50%), radial-gradient(circle at 50% 10%, #EFE3CE 0%, transparent 40%)',
            filter: 'blur(40px)'
          }}>
        </div>
        
        <div className="absolute inset-0 pointer-events-none z-[0] opacity-[0.3]" 
          style={{
            backgroundImage: `
              radial-gradient(circle at 10% 20%, #D4AF37 0.5px, transparent 0.5px),
              radial-gradient(circle at 40% 60%, #D4AF37 1px, transparent 1px),
              radial-gradient(circle at 80% 30%, #D4AF37 0.5px, transparent 0.5px),
              radial-gradient(circle at 20% 80%, #C5A028 1px, transparent 1px),
              radial-gradient(circle at 70% 90%, #D4AF37 0.5px, transparent 0.5px),
              radial-gradient(circle at 90% 10%, #D4AF37 1px, transparent 1px)
            `,
            backgroundSize: '150px 150px'
          }}>
        </div>

        <div className="absolute inset-0 pointer-events-none z-[0] mix-blend-multiply opacity-[0.04]" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}>
        </div>
        <div className="absolute inset-0 pointer-events-none z-[0] mix-blend-color-burn opacity-[0.02]" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='fiberFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.02 0.2' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23fiberFilter)'/%3E%3C/svg%3E")`,
          }}>
        </div>

        <div className="absolute inset-0 pointer-events-none z-[0] overflow-hidden opacity-60">
          <svg width="100%" height="100%" viewBox="0 0 800 1000" preserveAspectRatio="xMidYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M 100 200 Q 150 150 250 250 Q 300 350 200 400 Q 50 350 100 200 Z" fill="#D4AF37" opacity="0.04" filter="blur(40px)" />
            <path d="M 600 700 Q 750 650 700 850 Q 550 900 500 750 Q 450 600 600 700 Z" fill="#E1C699" opacity="0.05" filter="blur(50px)" />
            <path d="M 700 150 Q 800 100 850 200 Q 750 300 650 250 Q 550 150 700 150 Z" fill="#D4AF37" opacity="0.03" filter="blur(45px)" />
            <path d="M 150 800 Q 50 750 100 600 Q 200 500 250 650 Q 350 800 150 800 Z" fill="#E1C699" opacity="0.04" filter="blur(35px)" />
          </svg>
        </div>

        <div className="absolute inset-0 pointer-events-none z-[0] shadow-[inset_0_0_150px_rgba(74,63,53,0.05)]"></div>
        <div className="absolute inset-0 pointer-events-none z-[0] shadow-[inset_0_0_50px_rgba(212,175,55,0.03)]"></div>
        
        {/* Double Gold Border (Safe Area Bound) */}
        <div className="absolute inset-0 border border-[#D4AF37]/40 pointer-events-none mx-[40px] mt-[20px] mb-[40px] z-[15]"></div>
        <div className="absolute inset-0 border border-[#D4AF37]/20 pointer-events-none mx-[48px] mt-[28px] mb-[48px] z-[15]"></div>

        {/* Minimal Corner Accents */}
        <div className="absolute top-[28px] left-[48px] w-4 h-4 border-t border-l border-[#D4AF37] pointer-events-none z-[15]"></div>
        <div className="absolute top-[28px] right-[48px] w-4 h-4 border-t border-r border-[#D4AF37] pointer-events-none z-[15]"></div>
        <div className="absolute bottom-[48px] left-[48px] w-4 h-4 border-b border-l border-[#D4AF37] pointer-events-none z-[15]"></div>
        <div className="absolute bottom-[48px] right-[48px] w-4 h-4 border-b border-r border-[#D4AF37] pointer-events-none z-[15]"></div>

        {/* Baby Carriage positioned in bottom-left safe area */}
        <div className="absolute bottom-[56px] left-[56px] w-[130px] z-10 opacity-90 mix-blend-multiply pointer-events-none">
          <div className="relative w-full h-full drop-shadow-[0_4px_12px_rgba(212,175,55,0.15)]">
            <img src="/assets/motifs/vintage_baby_carriage.jpg" alt="Vintage Carriage" className="w-full h-auto object-contain" />
          </div>
        </div>

        {/* Main Content Flow */}
        <div className="relative z-10 flex flex-col items-center w-full mx-auto h-full flex-1">
          
          <DecorationLayer />

          {/* Title Area (pushed down to clear decorations) */}
          <div className="w-full flex flex-col items-center justify-center z-10 mt-[200px]">
            <p className="text-[#D4AF37] tracking-[0.2em] text-[10px] uppercase font-serif opacity-90 mb-3">
              PLEASE JOIN US FOR A
            </p>
            <h2 className="text-[#4A3F35] font-serif text-[42px] tracking-[0.1em] uppercase font-normal leading-[1.0] mb-2">
              BABY
            </h2>
            <h2 className="text-[#4A3F35] font-serif text-[42px] tracking-[0.1em] uppercase font-normal leading-[1.0] mb-6">
              SHOWER
            </h2>
            <p className="text-[#D4AF37] font-serif uppercase text-[10px] opacity-90 tracking-[0.2em] flex items-center justify-center gap-2">
              <span className="w-6 h-[1px] bg-[#D4AF37]/60"></span>
              HONORING THE MOTHER-TO-BE
              <span className="w-6 h-[1px] bg-[#D4AF37]/60"></span>
            </p>
          </div>

          {/* Mother Name */}
          <div className="w-[75%] max-w-[75%] flex flex-col items-center justify-center overflow-hidden z-10 mt-[20px]">
            <h1 
              className="font-light text-[#4A3F35] text-center w-full" 
              style={{ 
                fontFamily: '"Great Vibes", "Alex Brush", cursive', 
                fontSize: 'clamp(46px, 9vw, 60px)',
                lineHeight: '1.1',
                whiteSpace: 'pre-line',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                paddingTop: '10px'
              }}
            >
              {hostName.replace(' & ', '\n').replace(' and ', '\n')}
            </h1>
            <Heart className="w-2 h-2 text-[#D4AF37] fill-[#D4AF37] mt-3 mb-1" />
          </div>

          {/* Hosted By */}
          <div className="w-full flex flex-col items-center justify-center mt-[24px]">
            <p className="text-[#4A3F35]/80 text-[10px] uppercase tracking-widest mb-1.5">Hosted with love by</p>
            <div className="flex items-center justify-center gap-3 w-full">
              <p className="text-[#4A3F35] text-[12px] tracking-[0.15em] uppercase font-serif">{hostedBy}</p>
            </div>
          </div>

          {/* Information Row */}
          <div className="w-full shrink-0 grid grid-cols-3 gap-0 border-y border-[#D4AF37]/20 mt-[32px] py-[24px]">
            
            {/* Column 1: Date */}
            <div className="flex flex-col items-center justify-center text-center p-2 border-r border-[#D4AF37]/20 h-full">
              <Calendar className="w-4 h-4 text-[#D4AF37] stroke-1 mb-2 shrink-0" />
              <p className="font-serif tracking-widest text-[#4A3F35] uppercase text-[11px] leading-tight mb-1">{format(eventDate, 'EEEE')}</p>
              <p className="font-serif text-[12px] text-[#4A3F35] tracking-wide uppercase leading-tight">{format(eventDate, 'MMMM d, yyyy')}</p>
            </div>

            {/* Column 2: Time */}
            <div className="flex flex-col items-center justify-center text-center p-2 border-r border-[#D4AF37]/20 h-full">
              <Clock className="w-4 h-4 text-[#D4AF37] stroke-1 mb-2 shrink-0" />
              <p className="font-serif text-[11px] text-[#4A3F35] uppercase tracking-widest leading-tight mb-1">AT {eventTime}</p>
              <p className="font-serif text-[12px] text-[#4A3F35] tracking-wide uppercase leading-tight">ONWARDS</p>
            </div>

            {/* Column 3: Venue */}
            <div className="flex flex-col items-center justify-center text-center p-2 h-full">
              <MapPin className="w-4 h-4 text-[#D4AF37] stroke-1 mb-2 shrink-0" />
              <p className="font-serif text-[11px] text-[#4A3F35] uppercase tracking-widest leading-tight mb-1 w-full break-words line-clamp-2">{venueName}</p>
              <p className="font-serif text-[10px] text-[#4A3F35] tracking-wide leading-tight">555 Celestial Way,<br />San Francisco, CA</p>
            </div>
          </div>

          <div className="h-[24px] shrink-0"></div>

          {/* Dress Code (height 55px) */}
          <div className="w-[70%] h-[55px] shrink-0 flex items-center justify-center">
            {dressCode && (
              <div className="w-full h-[40px] border border-[#D4AF37]/40 rounded-full px-[18px] py-[12px] flex items-center justify-center gap-3">
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#D4AF37] font-serif flex items-center gap-2">
                  <GoldStar className="w-3 h-3 shrink-0" />
                  DRESS CODE: <span className="text-[11px] text-[#4A3F35]">{dressCode}</span>
                  <GoldStar className="w-3 h-3 shrink-0" />
                </p>
              </div>
            )}
          </div>

          <div className="h-[24px] shrink-0"></div>

          {/* Quote (height 70px) */}
          <div className="w-[75%] h-[70px] shrink-0 flex items-center justify-center text-center">
            <p className="text-[13px] font-serif text-[#4A3F35]/90 leading-relaxed italic">
              "{message}"
            </p>
          </div>

          <div className="h-[28px] shrink-0"></div>

          {/* RSVP (height 80px) */}
          <div className="w-full h-[80px] shrink-0 flex items-center justify-center">
            <div className="relative group flex items-center justify-center">
              <div className="absolute w-[250px] h-[56px] bg-gradient-to-r from-[#DAB46A] via-[#EBD9B4] to-[#DAB46A] rounded-[30px] blur-md opacity-30 group-hover:opacity-60 transition-opacity duration-700 pointer-events-none"></div>
              <button
                type="button"
                className="relative flex items-center justify-center gap-2 w-[250px] h-[56px] rounded-[30px] bg-gradient-to-r from-[#DAB46A] via-[#EBD9B4] to-[#DAB46A] bg-[length:200%_auto] text-white font-serif uppercase tracking-[0.2em] text-[14px] shadow-[0_4px_20px_rgba(212,175,55,0.25)] hover:bg-[position:right_center] hover:-translate-y-0.5 transition-all duration-500 border border-white/30"
                onClick={() => setShowRsvpModal(true)}
              >
                <span className="text-white text-[16px] font-light">♡</span>
                <span className="mt-[1px]">RSVP NOW</span>
                <span className="text-white text-[16px] font-light">♡</span>
              </button>
            </div>
          </div>

          <div className="h-[20px] shrink-0"></div>

          {/* Footer (height 45px) */}
          <div className="w-full h-[45px] shrink-0 flex flex-col items-center justify-center gap-1.5">
            <p className="text-[11px] font-serif text-[#4A3F35] tracking-widest uppercase text-center opacity-90">
              We can't wait to celebrate with you!
            </p>
            <Heart className="w-1.5 h-1.5 text-[#D4AF37] fill-[#D4AF37] opacity-70" />
          </div>

        </div>
      </div>

      {showRsvpModal && (
        <PremiumRsvpModal
          invitationId={invitationId}
          isLive={isLive}
          onClose={() => setShowRsvpModal(false)}
        />
      )}
    </div>
  );
}

registry.register({ metadata: META, component: ModernLuxuryBabyShower });
